import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { store } from 'react-notifications-component';
import { Button as MIButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import styles from './cities-list.module.scss';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { getAllCity } from '../../../shared/services/data/lookups.data';
import { CityModel } from '../../../shared/models/city.model';
import { updateCity } from '../../shared/services/data.service';
import { MODULE_NAME } from '../../shared/config/city-module.config';
import { EditCityModal } from './components/edit-city-modal/edit-city-modal.component';

/**
 * functional component CitiesListContainer
 */
export const CitiesListContainer: React.FC = () => {
  const [cities, setCities] = useState<CityModel[]>([]);
  const [cityToUpdate, setCityToUpdate] = useState<CityModel>();
  const { t } = useTranslation();

  useEffect(() => {
    getAllCity(MODULE_NAME).then((response: CityModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setCities(response);
      }
    });

    return () => {
      store.removeNotification('edit-city-failed');
    };
  }, []);

  const handleEditCitySubmit = (cityId: string, city: Pick<CityModel, 'name' | 'nameAr'>) => {
    store.removeNotification('edit-city-failed');
    updateCity(cityId, city).then((response: CityModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        const isPermissionError = response.status === 401 || response.status === 403;
        store.addNotification({
          id: 'edit-city-failed',
          title: t(
            isPermissionError
              ? 'CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_PERMISSION.TITLE'
              : 'CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_FAILED.TITLE'
          ),
          message: t(
            isPermissionError
              ? 'CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_PERMISSION.MESSAGE'
              : 'CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_FAILED.MESSAGE'
          ),
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut']
        });
        return;
      }
      store.addNotification({
        title: t('CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_SUCCESS.TITLE'),
        message: t('CITY_MODULE.LIST_CONTAINER.NOTIFICATIONS.EDIT_CITY_SUCCESS.MESSAGE'),
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: { duration: 3000, onScreen: true }
      });
      setCities(cities.map((item) => (item.id === cityId ? { ...item, ...city } : item)));
    });
  };

  return (
    <div className={styles['cities-list-container']}>
      <div className={styles['cities-list-container__title']}>
        {t('CITY_MODULE.LIST_CONTAINER.TITLE')}
      </div>
      <div className={styles['cities-list-container__list-wrapper']}>
        {cities.map((city) => (
          <div key={city.id} className={styles['cities-list-container__city-item']}>
            <div className={styles['cities-list-container__city-item__names']}>
              <span>{city.name}</span>
              <span className={styles['cities-list-container__city-item__name-ar']}>
                {city.nameAr || '—'}
              </span>
            </div>
            <MIButton onClick={() => setCityToUpdate(city)}>
              <EditIcon fontSize="large" color="inherit" />
            </MIButton>
          </div>
        ))}
      </div>
      <EditCityModal
        config={{ showModal: !!cityToUpdate }}
        data={{ city: cityToUpdate }}
        eventHandlers={{
          cancelHandler: () => setCityToUpdate(undefined),
          editCityHandler: (cityId, city) => {
            setCityToUpdate(undefined);
            handleEditCitySubmit(cityId, city);
          }
        }}
      />
    </div>
  );
};

export default CitiesListContainer;
